import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import {
  Search,
  RefreshCw,
  Download,
  CreditCard,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  Eye,
  ExternalLink,
  Copy,
  Check,
  User,
  Calendar,
  DollarSign,
  ShieldCheck,
  X
} from "lucide-react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../config";

export default function TransactionHistory() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({
    total_count: 0,
    active_count: 0,
    pending_count: 0,
    failed_count: 0,
    expired_count: 0,
    total_revenue: 0
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Fetch transactions from backend
  const fetchTransactions = async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/user-subscriptions/transactions`);
      const resJson = await response.json();

      if (response.ok && resJson.status) {
        setTransactions(resJson.data || []);
        if (resJson.stats) {
          setStats(resJson.stats);
        }
      } else {
        throw new Error(resJson.message || "Failed to fetch transactions");
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      Swal.fire("Error", error.message || "Failed to load transaction history", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  // Filtered transactions
  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => {
      // Status filter
      if (selectedStatus !== "all" && t.status !== selectedStatus) {
        return false;
      }

      // Search filter
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();

      const name = (t.fullname || "").toLowerCase();
      const mobile = (t.mobile_number || "").toLowerCase();
      const userId = String(t.user_id || "");
      const transId = String(t.transaction_id || t.id || "");
      const planName = (t.plan_name || "").toLowerCase();
      const orderId = (t.razorpay_order_id || "").toLowerCase();
      const paymentId = (t.razorpay_payment_id || "").toLowerCase();
      const businessName = (t.business_name || "").toLowerCase();

      return (
        name.includes(q) ||
        mobile.includes(q) ||
        userId.includes(q) ||
        transId.includes(q) ||
        planName.includes(q) ||
        orderId.includes(q) ||
        paymentId.includes(q) ||
        businessName.includes(q)
      );
    });
  }, [transactions, selectedStatus, searchTerm]);

  // Copy to clipboard helper
  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (filteredTransactions.length === 0) {
      Swal.fire("No Data", "No transactions to export.", "info");
      return;
    }

    const exportData = filteredTransactions.map((t, idx) => ({
      "#": idx + 1,
      "Transaction ID": t.transaction_id || t.id,
      "User ID": t.user_id,
      "User Name": t.fullname || "N/A",
      "Mobile Number": t.mobile_number || "N/A",
      "Business Name": t.business_name || "N/A",
      "Plan Name": t.plan_name || "N/A",
      "Price (INR)": parseFloat(t.price || 0).toFixed(2),
      "Duration (Days)": t.duration_days || "N/A",
      "Payment Status": (t.status || "pending").toUpperCase(),
      "Order ID": t.razorpay_order_id || "N/A",
      "Payment ID": t.razorpay_payment_id || "N/A",
      "Start Date": t.start_date ? new Date(t.start_date).toLocaleString() : "N/A",
      "End Date": t.end_date ? new Date(t.end_date).toLocaleString() : "N/A",
      "Created At": t.created_at ? new Date(t.created_at).toLocaleString() : "N/A"
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
    XLSX.writeFile(workbook, `Transactions_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Format currency
  const formatCurrency = (val) => {
    const num = parseFloat(val || 0);
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2
    }).format(num);
  };

  // Status badge helper
  const renderStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Active / Paid
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800">
            <XCircle className="w-3 h-3" />
            Failed
          </span>
        );
      case "expired":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-700/60 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
            Expired
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 capitalize">
            {status || "Unknown"}
          </span>
        );
    }
  };

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans relative overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
        <Header
          onMenuClick={() => setIsSidebarOpen(true)}
          title="Transaction History"
          subtitle="View and monitor all user subscription payments, orders, and plan activations."
        />

        <div className="p-4 sm:p-8 flex-1 w-full mx-auto space-y-6">
          {/* Top Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Transactions */}
            <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Total Transactions
                </p>
                <h3 className="text-2xl font-bold text-slate-800 dark:text-white mt-1">
                  {stats.total_count || transactions.length}
                </h3>
              </div>
              <div className="p-3 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-xl">
                <CreditCard className="w-6 h-6" />
              </div>
            </div>

            {/* Total Revenue */}
            <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Active Revenue
                </p>
                <h3 className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                  {formatCurrency(stats.total_revenue)}
                </h3>
              </div>
              <div className="p-3 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-xl">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>

            {/* Active Paid Subscriptions */}
            <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Active Paid Plans
                </p>
                <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                  {stats.active_count || 0}
                </h3>
              </div>
              <div className="p-3 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-xl">
                <CheckCircle2 className="w-6 h-6" />
              </div>
            </div>

            {/* Pending Transactions */}
            <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Pending Orders
                </p>
                <h3 className="text-2xl font-bold text-amber-500 mt-1">
                  {stats.pending_count || 0}
                </h3>
              </div>
              <div className="p-3 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-xl">
                <Clock className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Action Bar: Search, Filters & Export */}
          <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by User Name, Mobile, User ID, Plan, Order ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl pl-10 pr-4 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:text-white placeholder-slate-400 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Status Tabs / Filters */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: "all", label: "All" },
                { key: "active", label: "Active" },
                { key: "pending", label: "Pending" },
                { key: "expired", label: "Expired" },
                { key: "failed", label: "Failed" }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setSelectedStatus(tab.key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                    selectedStatus === tab.key
                      ? "bg-blue-600 text-white shadow-sm"
                      : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                  }`}
                >
                  {tab.label}
                </button>
              ))}

              <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-1 hidden sm:block" />

              {/* Refresh Button */}
              <button
                onClick={() => fetchTransactions(true)}
                disabled={refreshing || loading}
                className="p-2 text-slate-500 hover:text-blue-600 dark:text-slate-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-xl transition-all"
                title="Refresh Transactions"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin text-blue-600" : ""}`} />
              </button>

              {/* Export Button */}
              <button
                onClick={handleExportExcel}
                className="flex items-center gap-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all"
                title="Export transactions as Excel"
              >
                <Download className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span>Export Excel</span>
              </button>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex flex-col items-center justify-center p-16 text-slate-400">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-sm">Loading transaction history...</p>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-16 text-center text-slate-400">
                <div className="p-4 bg-slate-100 dark:bg-slate-700/50 rounded-full mb-3">
                  <CreditCard className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-base font-semibold text-slate-700 dark:text-slate-200 mb-1">
                  No Transactions Found
                </h3>
                <p className="text-sm text-slate-400 max-w-sm">
                  {searchTerm || selectedStatus !== "all"
                    ? "No transactions matched your current filters."
                    : "No subscription transactions have been recorded yet."}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/60 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      <th className="py-4 px-5"># ID</th>
                      <th className="py-4 px-5">User Details</th>
                      <th className="py-4 px-5">Plan & Price</th>
                      <th className="py-4 px-5">Payment & Gateway</th>
                      <th className="py-4 px-5">Status</th>
                      <th className="py-4 px-5">Date & Validity</th>
                      <th className="py-4 px-5 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50 text-sm">
                    {filteredTransactions.map((tx, index) => {
                      const transId = tx.transaction_id || tx.id;
                      return (
                        <tr
                          key={transId}
                          className="hover:bg-slate-50/80 dark:hover:bg-slate-700/30 transition-colors"
                        >
                          {/* Transaction ID */}
                          <td className="py-4 px-5 font-semibold text-slate-700 dark:text-slate-300">
                            #{transId}
                          </td>

                          {/* User Details */}
                          <td className="py-4 px-5">
                            <div className="flex items-center gap-3">
                              {/* Avatar */}
                              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-sm overflow-hidden flex-shrink-0 border border-blue-200 dark:border-blue-800">
                                {tx.userimage ? (
                                  <img
                                    src={tx.userimage}
                                    alt={tx.fullname || "User"}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  (tx.fullname || "U")[0].toUpperCase()
                                )}
                              </div>

                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-semibold text-slate-900 dark:text-white truncate">
                                    {tx.fullname || "Unnamed User"}
                                  </span>
                                  {tx.user_id && (
                                    <button
                                      onClick={() => navigate(`/users/${tx.user_id}`)}
                                      className="text-[11px] bg-slate-100 dark:bg-slate-700 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded hover:underline inline-flex items-center gap-0.5"
                                      title="Open User Profile"
                                    >
                                      ID: {tx.user_id}
                                      <ExternalLink className="w-2.5 h-2.5" />
                                    </button>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                  <span>{tx.mobile_number || "No mobile"}</span>
                                  {tx.mobile_number && (
                                    <button
                                      onClick={() => handleCopy(tx.mobile_number, `mobile_${transId}`)}
                                      className="text-slate-400 hover:text-slate-600"
                                      title="Copy mobile number"
                                    >
                                      {copiedField === `mobile_${transId}` ? (
                                        <Check className="w-3 h-3 text-emerald-500" />
                                      ) : (
                                        <Copy className="w-3 h-3" />
                                      )}
                                    </button>
                                  )}
                                </div>
                                {tx.business_name && (
                                  <span className="text-[11px] text-slate-400 truncate block">
                                    {tx.business_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Plan & Price */}
                          <td className="py-4 px-5">
                            <div>
                              <span className="inline-block px-2.5 py-0.5 rounded-lg text-xs font-bold bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800">
                                {tx.plan_name || "Custom Plan"}
                              </span>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-base font-bold text-slate-900 dark:text-white">
                                  {formatCurrency(tx.price)}
                                </span>
                                {tx.duration_days && (
                                  <span className="text-xs text-slate-400">
                                    / {tx.duration_days} days
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>

                          {/* Payment & Gateway */}
                          <td className="py-4 px-5">
                            <div className="space-y-1 text-xs">
                              {tx.razorpay_order_id ? (
                                <div className="flex items-center gap-1.5 font-mono text-[11px] text-slate-600 dark:text-slate-300">
                                  <span className="text-slate-400">Order:</span>
                                  <span className="truncate max-w-[140px]" title={tx.razorpay_order_id}>
                                    {tx.razorpay_order_id}
                                  </span>
                                  <button
                                    onClick={() => handleCopy(tx.razorpay_order_id, `order_${transId}`)}
                                    className="text-slate-400 hover:text-slate-600"
                                  >
                                    {copiedField === `order_${transId}` ? (
                                      <Check className="w-3 h-3 text-emerald-500" />
                                    ) : (
                                      <Copy className="w-3 h-3" />
                                    )}
                                  </button>
                                </div>
                              ) : (
                                <span className="text-slate-400 italic">No Order ID</span>
                              )}

                              {tx.razorpay_payment_id ? (
                                <div className="flex items-center gap-1.5 font-mono text-[11px] text-emerald-600 dark:text-emerald-400">
                                  <span className="text-slate-400">Pay ID:</span>
                                  <span className="truncate max-w-[140px]" title={tx.razorpay_payment_id}>
                                    {tx.razorpay_payment_id}
                                  </span>
                                  <button
                                    onClick={() => handleCopy(tx.razorpay_payment_id, `pay_${transId}`)}
                                    className="text-slate-400 hover:text-slate-600"
                                  >
                                    {copiedField === `pay_${transId}` ? (
                                      <Check className="w-3 h-3 text-emerald-500" />
                                    ) : (
                                      <Copy className="w-3 h-3" />
                                    )}
                                  </button>
                                </div>
                              ) : (
                                <span className="text-slate-400 italic text-[11px] block">
                                  Payment Pending
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="py-4 px-5">
                            {renderStatusBadge(tx.status)}
                          </td>

                          {/* Date & Validity */}
                          <td className="py-4 px-5">
                            <div className="text-xs space-y-0.5">
                              <p className="font-medium text-slate-700 dark:text-slate-300">
                                {tx.created_at
                                  ? new Date(tx.created_at).toLocaleDateString("en-IN", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric"
                                    })
                                  : "N/A"}
                              </p>
                              <p className="text-[11px] text-slate-400">
                                {tx.created_at
                                  ? new Date(tx.created_at).toLocaleTimeString("en-IN", {
                                      hour: "2-digit",
                                      minute: "2-digit"
                                    })
                                  : ""}
                              </p>
                              {tx.start_date && tx.end_date && (
                                <p className="text-[11px] text-emerald-600 dark:text-emerald-400">
                                  Valid till: {new Date(tx.end_date).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}
                                </p>
                              )}
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="py-4 px-5 text-right">
                            <button
                              onClick={() => setSelectedTransaction(tx)}
                              className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-colors inline-flex items-center gap-1 text-xs font-semibold"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                              <span>Details</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* TRANSACTION DETAILS MODAL */}
      {selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-lg">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800 dark:text-white">
                    Transaction #{selectedTransaction.transaction_id || selectedTransaction.id}
                  </h3>
                  <span className="text-xs text-slate-400">
                    {selectedTransaction.created_at
                      ? new Date(selectedTransaction.created_at).toLocaleString("en-IN")
                      : ""}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedTransaction(null)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4 text-sm max-h-[75vh] overflow-y-auto">
              {/* Payment Summary Box */}
              <div className="p-4 bg-gradient-to-br from-slate-50 to-blue-50/30 dark:from-slate-900 dark:to-blue-950/20 rounded-xl border border-slate-200/80 dark:border-slate-700 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold">Amount Paid</p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white mt-0.5">
                    {formatCurrency(selectedTransaction.price)}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                    Plan: {selectedTransaction.plan_name || "N/A"}
                  </p>
                </div>
                <div>{renderStatusBadge(selectedTransaction.status)}</div>
              </div>

              {/* User Section */}
              <div className="space-y-2 border-t border-slate-100 dark:border-slate-700 pt-3">
                <h4 className="text-xs font-bold uppercase text-slate-400 tracking-wider">
                  User Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-xs bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-slate-200/60 dark:border-slate-700/60">
                  <div>
                    <span className="text-slate-400 block">Full Name:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {selectedTransaction.fullname || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Mobile Number:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {selectedTransaction.mobile_number || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">User ID:</span>
                    <button
                      onClick={() => {
                        setSelectedTransaction(null);
                        navigate(`/users/${selectedTransaction.user_id}`);
                      }}
                      className="text-blue-600 dark:text-blue-400 font-semibold hover:underline inline-flex items-center gap-1"
                    >
                      #{selectedTransaction.user_id}
                      <ExternalLink className="w-2.5 h-2.5" />
                    </button>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Business Name:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {selectedTransaction.business_name || "N/A"}
                    </span>
                  </div>
                  {selectedTransaction.email && (
                    <div className="col-span-2">
                      <span className="text-slate-400 block">Email:</span>
                      <span className="font-semibold text-slate-800 dark:text-slate-200">
                        {selectedTransaction.email}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Gateway & Order Section */}
              <div className="space-y-2 border-t border-slate-100 dark:border-slate-700 pt-3">
                <h4 className="text-xs font-bold uppercase text-slate-400 tracking-wider">
                  Gateway Details
                </h4>
                <div className="space-y-2 text-xs bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-slate-200/60 dark:border-slate-700/60 font-mono">
                  <div>
                    <span className="text-slate-400 block font-sans">Razorpay Order ID:</span>
                    <span className="text-slate-800 dark:text-slate-200 break-all">
                      {selectedTransaction.razorpay_order_id || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-sans">Razorpay Payment ID:</span>
                    <span className="text-slate-800 dark:text-slate-200 break-all">
                      {selectedTransaction.razorpay_payment_id || "Pending"}
                    </span>
                  </div>
                  {selectedTransaction.razorpay_signature && (
                    <div>
                      <span className="text-slate-400 block font-sans">Signature:</span>
                      <span className="text-slate-500 break-all text-[10px]">
                        {selectedTransaction.razorpay_signature}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Subscription Validity */}
              <div className="space-y-2 border-t border-slate-100 dark:border-slate-700 pt-3">
                <h4 className="text-xs font-bold uppercase text-slate-400 tracking-wider">
                  Validity Period
                </h4>
                <div className="grid grid-cols-2 gap-3 text-xs bg-slate-50 dark:bg-slate-900/60 p-3 rounded-xl border border-slate-200/60 dark:border-slate-700/60">
                  <div>
                    <span className="text-slate-400 block">Start Date:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {selectedTransaction.start_date
                        ? new Date(selectedTransaction.start_date).toLocaleDateString("en-IN")
                        : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">End Date:</span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {selectedTransaction.end_date
                        ? new Date(selectedTransaction.end_date).toLocaleDateString("en-IN")
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
              <button
                type="button"
                onClick={() => {
                  setSelectedTransaction(null);
                  navigate(`/users/${selectedTransaction.user_id}`);
                }}
                className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
              >
                <span>View User Profile</span>
                <ExternalLink className="w-3 h-3" />
              </button>

              <button
                type="button"
                onClick={() => setSelectedTransaction(null)}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
